import React from 'react';
import { Helmet } from 'react-helmet-async';

// Primary SEO Keywords (not displayed, only for meta)
const PRIMARY_KEYWORDS = [
  "Laptop repair Aylesbury",
  "PC repair Aylesbury", 
  "Computer repair Aylesbury",
  "IT support Aylesbury",
  "Laptop diagnostics Aylesbury",
  "Free laptop diagnostic Aylesbury",
  "SSD upgrade Aylesbury",
  "Laptop screen replacement Aylesbury",
  "Data recovery Aylesbury",
  "Virus removal Aylesbury",
  "Overheating laptop repair Aylesbury",
  "Computer cleaning service Aylesbury",
  "PC running slow fix Aylesbury",
  "Home IT support Aylesbury",
  "Small business IT support Aylesbury"
];

// Service Areas (not displayed, only for schema/meta)
const SERVICE_AREAS = [
  "Aylesbury", "Buckingham", "Bicester", "Oxford", "Milton Keynes",
  "High Wycombe", "Tring", "Leighton Buzzard", "Waddesdon", "Haddenham",
  "Thame", "Princes Risborough", "Wendover", "Stoke Mandeville", 
  "Aston Clinton", "Stone", "Wing", "Great Missenden", "Chinnor", "Long Crendon"
];

const SEO = ({ 
  title = "Laptop Repair Aylesbury | PC Repair & Computer Services | SmartIT",
  description = "Expert laptop repair and PC repair in Aylesbury. FREE diagnostic, SSD upgrades, virus removal, data recovery, screen replacement. Serving Buckingham, Milton Keynes, Oxford, High Wycombe & surrounding areas.",
  keywords = PRIMARY_KEYWORDS.join(", "),
  image = "https://thesmartit.co.uk/og-image.png",
  url = "https://thesmartit.co.uk/",
  type = "website",
  schema = null
}) => {
  const siteName = "SmartIT - Laptop & PC Repair Aylesbury";
  const fullImageUrl = image.startsWith('http') ? image : `https://thesmartit.co.uk${image}`;
  
  // Enhanced LocalBusiness Schema with all service areas
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://thesmartit.co.uk/#business",
    "name": "SmartIT - Laptop & PC Repair Aylesbury",
    "alternateName": ["SmartIT", "Smart IT Solutions", "SmartIT Aylesbury"],
    "description": "Professional laptop repair, PC repair, and IT support services in Aylesbury. Offering free diagnostics, SSD upgrades, virus removal, data recovery, and screen replacement. Serving Buckinghamshire and surrounding areas.",
    "url": "https://thesmartit.co.uk",
    "telephone": "+447435324947",
    "email": "smartit_itsolutions@outlook.com",
    "image": "https://thesmartit.co.uk/og-image.png",
    "logo": "https://thesmartit.co.uk/og-image.png",
    "priceRange": "££",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Aylesbury",
      "addressRegion": "Buckinghamshire",
      "addressCountry": "GB",
      "postalCode": "HP19"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.8168,
      "longitude": -0.8084
    },
    "areaServed": SERVICE_AREAS.map(area => ({
      "@type": "City",
      "name": area,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": area === "Oxford" ? "Oxfordshire" : area === "Milton Keynes" ? "Buckinghamshire" : "Buckinghamshire"
      }
    })),
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 51.8168,
        "longitude": -0.8084
      },
      "geoRadius": "50000"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "IT Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Laptop Repair Aylesbury",
            "description": "Professional laptop repair service including screen replacement, keyboard repair, battery replacement, and motherboard repairs."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "PC Repair Aylesbury",
            "description": "Desktop PC repair, upgrades, custom builds, and troubleshooting for home and business users."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Free Laptop Diagnostic",
            "description": "Complimentary diagnostic service to identify issues with your laptop or PC before any repair."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SSD Upgrade Aylesbury",
            "description": "Speed up your laptop or PC with an SSD upgrade. Data migration included."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Virus Removal Aylesbury",
            "description": "Complete virus and malware removal service with security setup."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Data Recovery Aylesbury",
            "description": "Professional data recovery from failed hard drives, SSDs, and corrupted storage."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Laptop Screen Replacement",
            "description": "Fast laptop screen replacement service for cracked or damaged displays."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Overheating Laptop Repair",
            "description": "Thermal paste replacement, fan cleaning, and cooling system repairs."
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    },
    "sameAs": [
      "https://www.facebook.com/smartitsolution2020"
    ]
  };

  // Website Schema for AI search engines
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SmartIT - Laptop & PC Repair Aylesbury",
    "alternateName": "SmartIT",
    "url": "https://thesmartit.co.uk",
    "description": "Professional laptop repair, PC repair and IT support in Aylesbury, Buckinghamshire. Free diagnostics, fast repairs, affordable prices.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://thesmartit.co.uk/diagnostic?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SmartIT",
      "url": "https://thesmartit.co.uk"
    }
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Computer Repair Service",
    "name": "Laptop & PC Repair Aylesbury",
    "description": "Expert laptop repair and PC repair services in Aylesbury. We fix all brands including Dell, HP, Lenovo, ASUS, Acer, Apple MacBook. Services include screen replacement, SSD upgrades, virus removal, data recovery, and overheating repairs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "SmartIT",
      "url": "https://thesmartit.co.uk"
    },
    "areaServed": SERVICE_AREAS.map(area => ({ "@type": "City", "name": area })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Computer Repair Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Laptop Repair" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "PC Repair" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Free Diagnostic" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "SSD Upgrade" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Virus Removal" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Data Recovery" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Screen Replacement" } }
      ]
    }
  };

  // FAQ Schema for AI search engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does laptop repair cost in Aylesbury?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Laptop repair costs vary depending on the issue. At SmartIT Aylesbury, we offer FREE diagnostics so you know the exact cost before any repair. Common repairs range from £30-£150. SSD upgrades from £60, screen replacement from £80, virus removal from £40."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer free laptop diagnostics in Aylesbury?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! SmartIT offers completely FREE laptop and PC diagnostics in Aylesbury. We'll identify the problem and give you a no-obligation quote before any repair work begins."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do you cover for PC repair?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SmartIT provides laptop and PC repair services across Aylesbury, Buckingham, Bicester, Oxford, Milton Keynes, High Wycombe, Tring, Leighton Buzzard, Waddesdon, Haddenham, Thame, Princes Risborough, Wendover, and surrounding areas in Buckinghamshire."
        }
      },
      {
        "@type": "Question",
        "name": "How long does a laptop repair take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most laptop repairs at SmartIT Aylesbury are completed within 24-48 hours. Simple fixes like virus removal or SSD upgrades can often be done same-day. We'll always give you an estimated turnaround time after the free diagnostic."
        }
      },
      {
        "@type": "Question",
        "name": "Can you fix an overheating laptop?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, overheating laptop repair is one of our specialties. We clean internal fans and vents, replace thermal paste, and repair or replace cooling systems. This service helps extend your laptop's life and improve performance."
        }
      }
    ]
  };

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary Meta Tags - Optimized for AI Search */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots & Indexing */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="GB-BKM" />
      <meta name="geo.placename" content="Aylesbury, Buckinghamshire" />
      <meta name="geo.position" content="51.8168;-0.8084" />
      <meta name="ICBM" content="51.8168, -0.8084" />
      
      {/* Language & Locale */}
      <meta httpEquiv="content-language" content="en-GB" />
      <meta name="language" content="English" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="SmartIT - Laptop and PC Repair Aylesbury - Free Diagnostics" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content="SmartIT - Laptop and PC Repair Aylesbury" />
      
      {/* Canonical */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {/* Structured Data - Website */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {/* Structured Data - Service */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      
      {/* Structured Data - FAQ */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      
      {/* Custom Schema if provided */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Page-specific SEO configurations - Each page has unique preview
export const pageSEO = {
  home: {
    title: "Laptop Repair Aylesbury | PC Repair & FREE Diagnostics | SmartIT",
    description: "Expert laptop repair and PC repair in Aylesbury. FREE diagnostic on all devices. SSD upgrades, virus removal, data recovery, screen replacement. Serving Buckingham, Milton Keynes, Oxford, High Wycombe.",
    keywords: "laptop repair Aylesbury, PC repair Aylesbury, computer repair Aylesbury, free laptop diagnostic Aylesbury, IT support Aylesbury, SSD upgrade Aylesbury, virus removal Aylesbury, data recovery Aylesbury",
    image: "https://thesmartit.co.uk/og-image.png",
    url: "https://thesmartit.co.uk/"
  },
  diagnostic: {
    title: "Free Laptop Diagnostic Aylesbury | PC Health Check | SmartIT",
    description: "Run a FREE comprehensive diagnostic on your laptop or PC. Check CPU, RAM, storage health. Get AI-powered repair recommendations. SmartIT Aylesbury specialists.",
    keywords: "free laptop diagnostic Aylesbury, laptop diagnostics Aylesbury, PC health check Aylesbury, computer diagnostic Aylesbury, laptop test Aylesbury",
    image: "https://thesmartit.co.uk/og-diagnostic.png",
    url: "https://thesmartit.co.uk/diagnostic"
  },
  remoteSupport: {
    title: "Remote IT Support Aylesbury | Home & Business | SmartIT",
    description: "Instant remote IT support for home and small business in Aylesbury. Fix computer problems remotely - fast, secure, convenient. Serving Buckinghamshire.",
    keywords: "remote IT support Aylesbury, home IT support Aylesbury, small business IT support Aylesbury, online computer help Aylesbury",
    image: "https://thesmartit.co.uk/og-remote-support.png",
    url: "https://thesmartit.co.uk/remote-support"
  },
  webDevelopment: {
    title: "Web Development Aylesbury | Website Design | SmartIT",
    description: "Professional website design and development in Aylesbury. Custom websites, e-commerce, business sites. Local web developer serving Buckinghamshire.",
    keywords: "web development Aylesbury, website design Aylesbury, web developer Buckinghamshire, small business website Aylesbury",
    image: "https://thesmartit.co.uk/og-web-development.png",
    url: "https://thesmartit.co.uk/web-development"
  },
  printingServices: {
    title: "Printing Services Aylesbury | Document Printing | SmartIT",
    description: "Professional document printing services in Aylesbury. Fast turnaround, competitive prices. Upload files and collect locally. SmartIT Printing.",
    keywords: "printing services Aylesbury, document printing Aylesbury, print shop Aylesbury",
    image: "https://thesmartit.co.uk/og-printing.png",
    url: "https://thesmartit.co.uk/printing-services"
  },
  pcBuilder: {
    title: "Custom PC Build Aylesbury | Gaming PC | Workstation | SmartIT",
    description: "Custom PC builds in Aylesbury. Gaming PCs, video editing workstations, office computers. AI-powered recommendations. Expert assembly and setup.",
    keywords: "custom PC build Aylesbury, gaming PC Aylesbury, PC builder Buckinghamshire, computer build service",
    image: "https://thesmartit.co.uk/og-pc-builder.png",
    url: "https://thesmartit.co.uk/pc-builder"
  },
  promoteYourSite: {
    title: "Digital Marketing Aylesbury | SEO & Social Media | SmartIT",
    description: "Grow your business online with SmartIT Aylesbury. Social media marketing, SEO, ad campaigns. Local digital marketing for Buckinghamshire businesses.",
    keywords: "digital marketing Aylesbury, SEO Aylesbury, social media marketing Buckinghamshire",
    image: "https://thesmartit.co.uk/og-promote.png",
    url: "https://thesmartit.co.uk/promote-your-site"
  },
  tutorials: {
    title: "PC Repair Tutorials | DIY Laptop Fixes | SmartIT Aylesbury",
    description: "Free PC repair tutorials and laptop maintenance guides. Learn simple fixes you can do at home. Health checks, overheating solutions, storage cleanup.",
    keywords: "PC repair tutorials, laptop maintenance guide, computer DIY fixes, laptop troubleshooting Aylesbury",
    image: "https://thesmartit.co.uk/og-tutorials.png",
    url: "https://thesmartit.co.uk/tutorials"
  },
  aboutSmartit: {
    title: "About SmartIT | Laptop & PC Repair Specialist Aylesbury",
    description: "SmartIT is a professional laptop and PC repair service in Aylesbury. Free diagnostics, fast repairs, honest pricing. Serving Buckingham, Milton Keynes, Oxford.",
    keywords: "about SmartIT, computer repair Aylesbury, laptop repair specialist, IT support Buckinghamshire",
    image: "https://thesmartit.co.uk/og-about.png",
    url: "https://thesmartit.co.uk/about-smartit"
  },
  login: {
    title: "Customer Login | SmartIT Aylesbury",
    description: "Sign in to your SmartIT customer portal. Track laptop repairs, view support tickets, manage IT services. Secure access for customers.",
    keywords: "SmartIT login, customer portal, repair tracking",
    image: "https://thesmartit.co.uk/og-login.png",
    url: "https://thesmartit.co.uk/login"
  },
  register: {
    title: "Create Account | SmartIT Aylesbury",
    description: "Create your SmartIT account to submit support tickets, track laptop repairs, and access IT services. Join SmartIT Aylesbury today.",
    keywords: "SmartIT register, create account, IT support portal",
    image: "https://thesmartit.co.uk/og-login.png",
    url: "https://thesmartit.co.uk/register"
  },
  privacyPolicy: {
    title: "Privacy Policy | SmartIT Aylesbury",
    description: "SmartIT Privacy Policy. Learn how we protect your data and personal information. Laptop repair and IT support services in Aylesbury.",
    keywords: "privacy policy, data protection, SmartIT",
    image: "https://thesmartit.co.uk/og-privacy.png",
    url: "https://thesmartit.co.uk/privacy-policy"
  },
  reviews: {
    title: "SmartIT Reviews – What Clients Say About Our IT Services",
    description: "Read verified client reviews for SmartIT laptop repair and PC repair services in Aylesbury. See why customers rate us 5 stars for computer repair and IT support.",
    keywords: "SmartIT reviews, laptop repair Aylesbury reviews, PC repair reviews, computer repair testimonials, IT support Aylesbury feedback",
    image: "https://thesmartit.co.uk/og-image.png",
    url: "https://thesmartit.co.uk/reviews"
  },
  sellDevice: {
    title: "Quick Laptop & PC Sale Service | Sell Your Device | SmartIT Aylesbury",
    description: "Sell your laptop or PC quickly to SmartIT Aylesbury. Free technical evaluation, no waiting on Marketplace, instant offers. We accept working and faulty devices.",
    keywords: "sell laptop Aylesbury, sell PC Aylesbury, sell computer, buy my laptop, quick device sale, sell faulty laptop, sell gaming PC",
    image: "https://thesmartit.co.uk/og-sell-device.png",
    url: "https://thesmartit.co.uk/sell-device"
  }
};

export default SEO;
